import { useCallback, useMemo, useState } from "react";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import { CalendarDays, ChevronsUpDown, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { isDateInRange } from "@/utils/exportTimesheets";
import { cn } from "@/lib/utils";

export const MONTH_OPTIONS = [
  { value: 0, label: "January", short: "Jan" },
  { value: 1, label: "February", short: "Feb" },
  { value: 2, label: "March", short: "Mar" },
  { value: 3, label: "April", short: "Apr" },
  { value: 4, label: "May", short: "May" },
  { value: 5, label: "June", short: "Jun" },
  { value: 6, label: "July", short: "Jul" },
  { value: 7, label: "August", short: "Aug" },
  { value: 8, label: "September", short: "Sep" },
  { value: 9, label: "October", short: "Oct" },
  { value: 10, label: "November", short: "Nov" },
  { value: 11, label: "December", short: "Dec" },
] as const;

export const filterTriggerClass =
  "h-10 gap-2 rounded-xl border-border/80 bg-background px-3 shadow-sm hover:bg-muted/50";

export const FilterCountBadge = ({ count }: { count: number }) => {
  if (count <= 0) return null;
  return (
    <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs text-white">
      {count}
    </span>
  );
};

const currentPeriod = () => {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() };
};

/**
 * Period selection shared by the admin tabs. Defaults to the current year and
 * month. Year/Month and Date range are alternative ways to pick the period:
 * choosing one clears the other.
 */
export const usePeriodFilter = () => {
  const [selectedYears, setSelectedYears] = useState<number[]>(() => [currentPeriod().year]);
  const [selectedMonths, setSelectedMonths] = useState<number[]>(() => [currentPeriod().month]);
  const [dateRange, setDateRangeState] = useState<DateRange | undefined>();

  const hasDateRange = Boolean(dateRange?.from);
  const isAllTime = !hasDateRange && selectedYears.length === 0 && selectedMonths.length === 0;

  // Decided per day, so a month runs from its 1st to its last day.
  const isDayInPeriod = useCallback(
    (date: Date) => {
      if (dateRange?.from) return isDateInRange(date, dateRange);
      const matchesYear =
        selectedYears.length === 0 || selectedYears.includes(date.getFullYear());
      const matchesMonth =
        selectedMonths.length === 0 || selectedMonths.includes(date.getMonth());
      return matchesYear && matchesMonth;
    },
    [dateRange, selectedYears, selectedMonths]
  );

  const dateRangeLabel = useMemo(() => {
    if (!dateRange?.from) return "Date range";
    if (!dateRange.to) return format(dateRange.from, "MMM dd, yyyy");
    return `${format(dateRange.from, "MMM dd")} – ${format(dateRange.to, "MMM dd, yyyy")}`;
  }, [dateRange]);

  const periodLabel = useMemo(() => {
    if (dateRange?.from) return dateRangeLabel;
    const yearPart =
      selectedYears.length === 0
        ? "All years"
        : selectedYears
            .slice()
            .sort((a, b) => b - a)
            .join(", ");
    const monthPart =
      selectedMonths.length === 0
        ? "All months"
        : selectedMonths
            .slice()
            .sort((a, b) => a - b)
            .map((month) => MONTH_OPTIONS[month].short)
            .join(", ");
    return `${monthPart} · ${yearPart}`;
  }, [dateRange, dateRangeLabel, selectedYears, selectedMonths]);

  const setYears = (years: number[]) => {
    setDateRangeState(undefined);
    setSelectedYears(years);
  };

  const setMonths = (months: number[]) => {
    setDateRangeState(undefined);
    setSelectedMonths(months);
  };

  const toggleYear = (year: number) =>
    setYears(
      selectedYears.includes(year)
        ? selectedYears.filter((item) => item !== year)
        : [...selectedYears, year]
    );

  const toggleMonth = (month: number) =>
    setMonths(
      selectedMonths.includes(month)
        ? selectedMonths.filter((item) => item !== month)
        : [...selectedMonths, month]
    );

  const setDateRange = (range: DateRange | undefined) => {
    setDateRangeState(range);
    if (range?.from) {
      setSelectedYears([]);
      setSelectedMonths([]);
    }
  };

  // Clearing the date range returns to the current month.
  const clearDateRange = () => {
    const period = currentPeriod();
    setDateRangeState(undefined);
    setSelectedYears([period.year]);
    setSelectedMonths([period.month]);
  };

  const clearAll = () => {
    setDateRangeState(undefined);
    setSelectedYears([]);
    setSelectedMonths([]);
  };

  return {
    selectedYears,
    selectedMonths,
    dateRange,
    hasDateRange,
    isAllTime,
    isDayInPeriod,
    dateRangeLabel,
    periodLabel,
    setYears,
    setMonths,
    toggleYear,
    toggleMonth,
    setDateRange,
    clearDateRange,
    clearAll,
  };
};

export type PeriodFilterState = ReturnType<typeof usePeriodFilter>;

interface PeriodFilterBarProps {
  period: PeriodFilterState;
  availableYears: number[];
  onRefresh?: () => void;
  isRefreshing?: boolean;
  lastUpdated?: Date | null;
}

export const PeriodFilterBar = ({
  period,
  availableYears,
  onRefresh,
  isRefreshing = false,
  lastUpdated,
}: PeriodFilterBarProps) => {
  const [isYearOpen, setIsYearOpen] = useState(false);
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);

  // Always offer the current year, plus any selected year not in the data.
  const yearOptions = useMemo(() => {
    const years = new Set<number>([
      currentPeriod().year,
      ...availableYears,
      ...period.selectedYears,
    ]);
    return [...years].sort((a, b) => b - a);
  }, [availableYears, period.selectedYears]);

  return (
    <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground">Period</p>
        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
          <span>{period.periodLabel}</span>
          {isRefreshing ? (
            <>
              <span aria-hidden>·</span>
              <Loader2 className="h-3 w-3 animate-spin text-primary" />
              <span className="text-primary">Loading data…</span>
            </>
          ) : (
            lastUpdated && (
              <>
                <span aria-hidden>·</span>
                <span>Updated {format(lastUpdated, "h:mm a")}</span>
              </>
            )
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 rounded-2xl border border-border/70 bg-muted/30 p-2">
        {onRefresh && (
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={onRefresh}
              disabled={isRefreshing}
              className="h-10 w-10 rounded-xl border-border/80 bg-background shadow-sm hover:bg-muted/50"
              aria-label="Refresh data"
              title="Refresh data"
            >
              <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
            </Button>
            <div className="mx-0.5 h-8 w-px bg-border" aria-hidden />
          </>
        )}

        <Popover open={isYearOpen} onOpenChange={setIsYearOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className={filterTriggerClass}>
              <CalendarDays className="h-4 w-4" />
              <span>Year</span>
              <FilterCountBadge count={period.selectedYears.length} />
              <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 animate-in fade-in-0 zoom-in-95" align="end">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Select Years</h4>
                <div className="flex space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => period.setYears([...yearOptions])}>
                    All
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => period.setYears([])}>
                    Clear
                  </Button>
                </div>
              </div>
              <div className="max-h-56 space-y-2 overflow-y-auto">
                {yearOptions.map((year) => (
                  <div key={year} className="flex items-center space-x-2">
                    <Checkbox
                      id={`period-year-${year}`}
                      checked={period.selectedYears.includes(year)}
                      onCheckedChange={() => period.toggleYear(year)}
                    />
                    <label htmlFor={`period-year-${year}`} className="cursor-pointer text-sm">
                      {year}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Popover open={isMonthOpen} onOpenChange={setIsMonthOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className={filterTriggerClass}>
              <CalendarDays className="h-4 w-4" />
              <span>Month</span>
              <FilterCountBadge count={period.selectedMonths.length} />
              <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 animate-in fade-in-0 zoom-in-95" align="end">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Select Months</h4>
                <div className="flex space-x-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => period.setMonths(MONTH_OPTIONS.map((month) => month.value))}
                  >
                    All
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => period.setMonths([])}>
                    Clear
                  </Button>
                </div>
              </div>
              <div className="grid max-h-56 grid-cols-2 gap-2 overflow-y-auto">
                {MONTH_OPTIONS.map((month) => (
                  <div key={month.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`period-month-${month.value}`}
                      checked={period.selectedMonths.includes(month.value)}
                      onCheckedChange={() => period.toggleMonth(month.value)}
                    />
                    <label
                      htmlFor={`period-month-${month.value}`}
                      className="cursor-pointer text-sm"
                    >
                      {month.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Popover open={isDateRangeOpen} onOpenChange={setIsDateRangeOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(filterTriggerClass, period.hasDateRange && "border-primary/40")}
            >
              <CalendarDays className="h-4 w-4" />
              <span className="max-w-[180px] truncate">{period.dateRangeLabel}</span>
              <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <h4 className="text-sm font-medium">Select date range</h4>
              <Button
                size="sm"
                variant="ghost"
                onClick={period.clearDateRange}
                disabled={!period.hasDateRange}
              >
                Clear
              </Button>
            </div>
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={period.dateRange?.from}
              selected={period.dateRange}
              onSelect={period.setDateRange}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>

        {!period.isAllTime && (
          <Button
            variant="ghost"
            size="sm"
            onClick={period.clearAll}
            className="h-10 rounded-xl text-muted-foreground"
          >
            All time
          </Button>
        )}
      </div>
    </div>
  );
};
